import { useRef, useLayoutEffect, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { BANNER_LAYER } from './modalLayers';
// ⚠️ 상태를 판정하는 술어를 여기서 다시 import하지 말 것 — 이 배너가 상태에서 파생하는 것은
//    **문구 모드 하나**이고 그 정본은 `lib/organizations`의 `verificationDisplayMode`다
//    (노출 여부도 그 값의 `'none'`이 정한다). `isManualReviewVerificationStatus`·
//    `verificationBand`를 여기 끼우면 같은 축을 두 곳이 판정하게 되고, 그 기제가 이 레포에서
//    세 번 결함을 만들었다(2026-10-20 — 그 함수 JSDoc이 경위의 정본).
import {
  REJECTION_REASON_LABELS,
  hasRequiredVerificationDocs,
  verificationDisplayMode,
} from '../lib/organizations';

interface VerificationPendingBannerProps {
  topOffset?: number;
  onHeightChange?: (height: number) => void;
}

/**
 * 기관 인증 심사 대기/불일치/거부 알림 배너
 * - PENDING (retrying): 자동 재시도 중 (노란색, 시계 아이콘)
 * - PENDING (mismatch): 국세청 정보 불일치 — 사용자 정정 필요 (주황색, 경고 아이콘)
 * - REJECTED: 슈퍼관리자 인증 거부 (빨간색, X 아이콘)
 * - unknown: web이 모르는 상태 (중립 회색, 시계 아이콘) — 노출 여부·폴백 방향의 사유는
 *   `lib/organizations`의 `verificationBand` JSDoc이 정본
 * GUIDANCE_AGENCY, SAFETY_MANAGEMENT_AGENCY 카테고리 조직에만 표시
 */
export default function VerificationPendingBanner({ topOffset = 0, onHeightChange }: VerificationPendingBannerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentMembership } = useAuthStore();

  // H-8e: organization 제거 → company 평면화 (agency* 필드로 이전)
  const company = currentMembership?.company;
  const category = company?.category;
  const verificationStatus = company?.agencyVerificationStatus;
  // ⚠️ 캐스트를 되살리지 말 것 — `auth.ts`가 이 칸을 shared union으로 선언하므로 필요 없다.
  //    여기 캐스트가 있던 동안 그것은 **검증 없는 단언**이었고(그 칸은 `string | null`이었다),
  //    `REJECTION_REASON_LABELS[...]` 조회가 union 밖 값이면 조용히 `undefined`를 내는 입구였다.
  //    그 안전의 소유자는 서버 DTO의 `IsIn` + 그 spec이다(사유는 `auth.ts`의 그 필드 JSDoc).
  const rejectionReasonCode = company?.agencyRejectionReasonCode ?? undefined;
  const rejectionMemo = company?.agencyRejectionReason;

  const isWorkplace = category === 'WORKPLACE';

  // ⚠️ 분류 기준과 「필수 서류를 냈는가」 판정을 여기 다시 적지 말 것 — 정본은
  //    `lib/organizations`의 `hasRequiredVerificationDocs`다. 종전엔 이 자리가 분류 규칙과 판정을
  //    **사본으로** 들고 있었고(그 주석이 「Organization.tsx와 동일한 기준」이라 자진 신고했다)
  //    형제 설정 화면은 같은 질문을 `verificationDocuments.length > 0`(**아무** 서류)로 답해
  //    **이미 갈려 있었다** — 그래서 기관이 사업자등록증만 올리면 설정 화면이 「지정서 제출 완료」라
  //    **거짓**을 말했다(2026-10-21 — 그 함수 JSDoc이 경위의 정본).
  const verificationDocuments = Array.isArray(company?.agencyVerificationDocuments)
    ? (company.agencyVerificationDocuments as Array<{ type?: string }>)
    : [];
  const hasRequiredDocs = hasRequiredVerificationDocs(verificationDocuments, category);

  // 검증 대상 카테고리(기술지도/안전관리/일반사업장)이고, 심사 대기/진행 상태일 때만 표시
  const isVerificationTarget =
    category === 'GUIDANCE_AGENCY' ||
    category === 'SAFETY_MANAGEMENT_AGENCY' ||
    category === 'WORKPLACE';

  // ⚠️ **상태를 여기 손열거하지 말 것** — 문구 모드와 노출 여부는 **한 자리**에서 나온다.
  //    종전엔 이 자리에 ① `['PENDING','INFO_MISMATCH','REJECTED'] ∪ 수동심사술어`라는 합성
  //    손열거(노출)와 ② 상태별 `===` 사슬(문구)이 **따로** 있었고, 그래서 shared union에 멤버가
  //    늘면 ①에서는 **배너가 통째로 사라지고** ②에서는 **문구가 약해졌다**(둘 다 2026-10-20에
  //    고쳤다 — 사유·경위는 `verificationDisplayMode` JSDoc이 정본).
  //    ⚠️ 모드가 `'none'`인 것과 `'unknown'`인 것은 **다르다** — 앞은 렌더하지 않는 것(인증이
  //    필요 없거나 끝났다), 뒤는 **web이 모르는 상태**라 중립 문구로 띄우는 것이다.
  const displayMode = verificationDisplayMode(verificationStatus, hasRequiredDocs);
  const shouldRender = isVerificationTarget && displayMode !== 'none';

  // 표시 안 할 때 height 0 알림
  useEffect(() => {
    if (!shouldRender) onHeightChange?.(0);
  }, [shouldRender, onHeightChange]);

  // 실제 높이 측정 (shouldRender일 때만)
  useLayoutEffect(() => {
    if (!shouldRender || !ref.current) return;
    const element = ref.current;
    const update = () => onHeightChange?.(element.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [shouldRender, onHeightChange, displayMode]);

  // `!shouldRender`와 같은 조건을 풀어 쓴 것이다 — 이 형태여야 tsc가 `displayMode`에서 `'none'`을
  // 걷어내고, 그래서 아래 `config[displayMode]`가 **모드마다 항목이 있는지**를 검사한다
  // (`shouldRender`는 boolean이라 그 narrowing을 전달하지 못한다). 되돌리지 말 것.
  //
  // ⚠️ **그 전수성의 소유자는 「로컬 tsc」 하나다 — CI는 web을 Vite(esbuild)로 빌드해 타입을
  //    보지 않는다**(형제 `ReleaseNotes.tsx`·`lib/organizations.ts`가 같은 자리에서 적는 한 줄).
  //    그리고 `config`의 10개 키를 재는 **spec은 0건**이다. ⇒ 아래 `config`가 초록인 것을
  //    **CI 보호로 인용하지 말 것**: 모드를 더하고 항목을 잊으면 CI는 통과하고 런타임에
  //    `c`가 `undefined`가 되어 이 컴포넌트가 죽는다. CI에서 그 축에 가장 가까운 것은
  //    `lib/__tests__/verificationStatus.test.ts`의 소스 스캔 래칫(모드 **union**이 늘었는지)이고,
  //    그것은 「이 `config`에 항목이 있는지」를 **재지 않는다**.
  if (!isVerificationTarget || displayMode === 'none') {
    return null;
  }

  const clockIcon = (
    <svg
      className="h-4 w-4 text-yellow-600"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
        clipRule="evenodd"
      />
    </svg>
  );

  const warningIcon = (
    <svg
      className="h-4 w-4 text-orange-600"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );

  const config = {
    'pending-retrying': {
      containerClass: 'bg-yellow-50 border-yellow-200',
      iconBgClass: 'bg-yellow-100',
      textClass: 'text-yellow-800',
      buttonClass:
        'text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:ring-offset-yellow-50 focus:ring-yellow-500',
      icon: clockIcon,
      title: isWorkplace ? '사업자 인증 진행 중' : '기관 인증 진행 중',
      description: '국세청 사업자 정보 확인 중입니다. 보통 몇 분 이내에 완료됩니다.',
      buttonLabel: '조직 설정',
      onClick: () => navigate('/settings/organization'),
    },
    'pending-info-mismatch': {
      containerClass: 'bg-orange-50 border-orange-200',
      iconBgClass: 'bg-orange-100',
      textClass: 'text-orange-800',
      buttonClass:
        'text-orange-800 bg-orange-100 hover:bg-orange-200 focus:ring-offset-orange-50 focus:ring-orange-500',
      icon: warningIcon,
      title: '사업자 정보 정정 필요',
      description: '입력한 사업자 정보가 국세청 기록과 일치하지 않습니다. 조직 설정에서 정보를 확인하고 정정해 주세요.',
      buttonLabel: '정보 정정하기',
      onClick: () => navigate('/settings/organization'),
    },
    'pending-review-timeout': {
      containerClass: 'bg-orange-50 border-orange-300',
      iconBgClass: 'bg-orange-100',
      textClass: 'text-orange-900',
      buttonClass:
        'text-orange-900 bg-orange-100 hover:bg-orange-200 focus:ring-offset-orange-50 focus:ring-orange-500',
      icon: warningIcon,
      title: isWorkplace ? '사업자 인증 서류 제출 필요' : '기관 인증 서류 제출 필요',
      description: isWorkplace
        ? '자동 검증이 일정 시간 이상 진행되지 않아 운영자 수동 심사로 전환되었습니다. 사업자등록증을 업로드해 주세요.'
        : '자동 검증이 일정 시간 이상 진행되지 않아 운영자 수동 심사로 전환되었습니다. 사업자등록증과 지정서를 업로드해 주세요.',
      buttonLabel: '서류 업로드하기',
      onClick: () => navigate('/settings/organization'),
    },
    'pending-review-timeout-submitted': {
      containerClass: 'bg-blue-50 border-blue-200',
      iconBgClass: 'bg-blue-100',
      textClass: 'text-blue-900',
      buttonClass:
        'text-blue-900 bg-blue-100 hover:bg-blue-200 focus:ring-offset-blue-50 focus:ring-blue-500',
      icon: clockIcon,
      title: isWorkplace ? '사업자 인증 심사 대기 중' : '기관 인증 심사 대기 중',
      description: '서류 제출 완료. 운영자 수동 심사를 기다리고 있습니다. 검토 후 안내드립니다.',
      buttonLabel: '조직 설정',
      onClick: () => navigate('/settings/organization'),
    },
    'pending-review-no-match': {
      containerClass: 'bg-orange-50 border-orange-300',
      iconBgClass: 'bg-orange-100',
      textClass: 'text-orange-900',
      buttonClass:
        'text-orange-900 bg-orange-100 hover:bg-orange-200 focus:ring-offset-orange-50 focus:ring-orange-500',
      icon: warningIcon,
      title: '기관 인증 서류 제출 필요',
      description: '국세청 사업자 정보는 확인되었으나 공시 데이터에 해당 기관이 등록되어 있지 않습니다. 지정서를 업로드하여 수동 심사를 요청해 주세요.',
      buttonLabel: '서류 업로드하기',
      onClick: () => navigate('/settings/organization'),
    },
    /*
      `REVIEW_AMBIGUOUS`의 **서류 미제출** 자리. 위 `no-match`와 **색·아이콘·제목·버튼이 같고
      본문만 다르다** — 할 일(지정서 업로드)은 같지만 **이유가 반대**이기 때문이다.
      `no-match`는 공시에서 **못 찾은** 것이고(Top1 < threshold) 이쪽은 **찾았는데 경쟁 후보가
      여럿**인 것이다(Top1·Top2가 둘 다 임계를 넘고 격차 < margin). 판정은
      `organization-verification.service.ts:172~198`에 있다.
      ⚠️ 그래서 이 항목을 지우고 `no-match`로 합치지 말 것 — 그 본문의 「등록되어 있지 않습니다」가
      이 상태에는 **거짓**이다. 2026-10-20까지 실제로 그렇게 보여 주고 있었다.
    */
    'pending-review-ambiguous': {
      containerClass: 'bg-orange-50 border-orange-300',
      iconBgClass: 'bg-orange-100',
      textClass: 'text-orange-900',
      buttonClass:
        'text-orange-900 bg-orange-100 hover:bg-orange-200 focus:ring-offset-orange-50 focus:ring-orange-500',
      icon: warningIcon,
      title: '기관 인증 서류 제출 필요',
      description:
        '공시 데이터에 유사한 기관이 여러 곳 확인되어 어느 곳인지 특정하지 못했습니다. 지정서를 업로드하면 운영자가 확인해 드립니다.',
      buttonLabel: '서류 업로드하기',
      onClick: () => navigate('/settings/organization'),
    },
    'pending-review-no-match-submitted': {
      containerClass: 'bg-blue-50 border-blue-200',
      iconBgClass: 'bg-blue-100',
      textClass: 'text-blue-900',
      buttonClass:
        'text-blue-900 bg-blue-100 hover:bg-blue-200 focus:ring-offset-blue-50 focus:ring-blue-500',
      icon: clockIcon,
      title: isWorkplace ? '사업자 인증 심사 대기 중' : '기관 인증 심사 대기 중',
      description: '지정서 제출 완료. 운영자 수동 심사를 기다리고 있습니다. 검토 후 안내드립니다.',
      buttonLabel: '조직 설정',
      onClick: () => navigate('/settings/organization'),
    },
    'pending-review-reapplied': {
      containerClass: 'bg-orange-50 border-orange-300',
      iconBgClass: 'bg-orange-100',
      textClass: 'text-orange-900',
      buttonClass:
        'text-orange-900 bg-orange-100 hover:bg-orange-200 focus:ring-offset-orange-50 focus:ring-orange-500',
      icon: warningIcon,
      title: '기관 인증 재신청 접수',
      description: '서류 재업로드로 재신청이 접수되었습니다. 운영자 수동 심사 대기 중입니다. 검토 후 안내드립니다.',
      buttonLabel: '조직 설정',
      onClick: () => navigate('/settings/organization'),
    },
    rejected: {
      containerClass: 'bg-red-50 border-red-200',
      iconBgClass: 'bg-red-100',
      textClass: 'text-red-800',
      buttonClass:
        'text-red-800 bg-red-100 hover:bg-red-200 focus:ring-offset-red-50 focus:ring-red-500',
      icon: (
        <svg
          className="h-4 w-4 text-red-600"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
      title: rejectionReasonCode
        ? REJECTION_REASON_LABELS[rejectionReasonCode]
        : '기관 인증 반려',
      description: rejectionMemo
        ? `사유: ${rejectionMemo} — 다시 인증 정보를 확인하고 재신청해 주세요.`
        : '다시 인증 정보를 확인하고 재신청해 주세요.',
      buttonLabel: '조직 설정',
      onClick: () => navigate('/settings/organization'),
    },
    /*
      web이 모르는 상태 — 위 `===` 사슬 어디에도 안 걸린 것. 종전에는 이 자리가
      `'pending-retrying'`이라 **「보통 몇 분 이내에 완료됩니다」라는 약속**을 모르는 상태에
      했고(= 거짓 약속), 그보다 먼저 `shouldRender` 손열거가 그 상태를 **아예 걸러** 배너가
      사라졌다. ⚠️ 그래서 이 항목을 `'pending-retrying'`으로 되돌리지 말 것 — 그러면 두 거짓 중
      하나로 되돌아간다.
      문구는 **약속하지 않고 안내만** 한다. 색은 `red`가 아니라 중립(`gray`)이고 아이콘은 시계다.
    */
    unknown: {
      containerClass: 'bg-gray-50 border-gray-200',
      iconBgClass: 'bg-gray-100',
      textClass: 'text-gray-800',
      buttonClass:
        'text-gray-800 bg-gray-100 hover:bg-gray-200 focus:ring-offset-gray-50 focus:ring-gray-500',
      // `clockIcon`을 재사용하지 않는다 — 그쪽은 `text-yellow-600`이라 중립 바탕에서 색이 튄다
      icon: (
        <svg
          className="h-4 w-4 text-gray-600"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
            clipRule="evenodd"
          />
        </svg>
      ),
      title: isWorkplace ? '사업자 인증 상태 확인 중' : '기관 인증 상태 확인 중',
      description: '인증 진행 상태를 가져오는 중입니다. 조직 설정에서 서류 제출 상태를 확인할 수 있습니다.',
      buttonLabel: '조직 설정',
      onClick: () => navigate('/settings/organization'),
    },
  };

  const c = config[displayMode];

  return (
    <div
      ref={ref}
      className={`fixed left-0 right-0 ${BANNER_LAYER.verification} border-b ${c.containerClass}`}
      style={{ top: topOffset }}
    >
      <div className="max-w-7xl mx-auto px-3 py-2 sm:py-2.5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-2 w-full">
          <div className="flex items-center flex-1 min-w-0">
            <span className={`flex p-1.5 rounded flex-shrink-0 ${c.iconBgClass}`}>{c.icon}</span>
            <p className={`ml-2 text-sm font-medium line-clamp-2 min-w-0 ${c.textClass}`}>
              <span className="font-semibold">{c.title}</span>
              <span className="hidden sm:inline"> — {c.description}</span>
            </p>
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={c.onClick}
              className={`text-sm font-semibold hover:underline focus:outline-none focus:underline flex-shrink-0 ${c.textClass}`}
            >
              {c.buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
